import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BookOpen, Plus, ExternalLink, Edit3, Trash2, Twitter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import JournalEntryModal from '@/components/JournalEntryModal';
import { shareEntryToX } from '@/utils/journalShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-stock Journal tab. Shows only this ticker's entries inline so
// the user can scan their own thinking history while researching the
// stock again. Heavy lifting (filters, search, weekly review) lives on
// the standalone /journal page — this tab links over for that.
//
// Round 79c — accepts an optional `onViewAll` prop. When passed (i.e.
// when rendered inside the StockModal), the "View all" link calls it
// instead of using a plain <Link>. The modal supplies a callback that
// encodes the modal's `fromPath` + `ticker` into history.state so the
// /journal page's Back button can restore the modal on this stock.
// Falls back to the plain <Link> when the prop isn't provided.

const HUE = {
  thesis_add:   'border-emerald-300 dark:border-emerald-800',
  thesis_sell:  'border-rose-300 dark:border-rose-800',
  dividend_cut: 'border-amber-300 dark:border-amber-800',
  ex_div:       'border-cyan-300 dark:border-cyan-800',
  free:         'border-slate-300 dark:border-slate-700',
  review:       'border-violet-300 dark:border-violet-800',
  jnl_starter:  'border-amber-300 dark:border-amber-800',
};
const TYPE_LABEL = {
  thesis_add: 'Thesis on Add',
  thesis_sell: 'Thesis on Sell',
  dividend_cut: 'Dividend Cut',
  ex_div: 'Ex-Div Week',
  free: 'Note',
  review: 'Review',
  jnl_starter: 'JNL Starter',
};

export default function StockJournalTab({ ticker, user, onLoginRequired, onViewAll }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchEntries = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/journal/entries`, {
        params: { ticker },
        withCredentials: true,
      });
      setEntries(data.entries || []);
    } catch (err) {
      // 401 = not logged in. Silently show empty state with login CTA.
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleNew = () => {
    if (!user) { onLoginRequired && onLoginRequired(); return; }
    setShowNew(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this journal entry permanently?')) return;
    try {
      await axios.delete(`${API}/journal/entries/${entryId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Delete failed', { description: err?.response?.data?.detail || err.message });
    }
  };

  return (
    <div className="space-y-3" data-testid={`stock-journal-tab-${ticker}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          <h3 className="text-sm font-bold">Your notes on {ticker}</h3>
          {entries.length > 0 && (
            <span className="text-xs text-muted-foreground">({entries.length})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onViewAll ? (
            <button
              type="button"
              onClick={onViewAll}
              className="text-xs text-muted-foreground hover:text-violet-600 inline-flex items-center gap-1"
              data-testid={`stock-journal-view-all-${ticker}`}
            >
              View all <ExternalLink className="w-3 h-3" />
            </button>
          ) : (
            <Link
              to="/journal"
              className="text-xs text-muted-foreground hover:text-violet-600 inline-flex items-center gap-1"
              data-testid={`stock-journal-view-all-${ticker}`}
            >
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          )}
          <Button
            size="sm"
            onClick={handleNew}
            data-testid={`stock-journal-new-btn-${ticker}`}
            className="bg-violet-600 hover:bg-violet-700 text-white h-8"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> New
          </Button>
        </div>
      </div>

      {!user && (
        <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
          <p className="text-sm text-muted-foreground">Sign in to journal your thinking on {ticker}.</p>
        </div>
      )}

      {user && loading && <div className="text-center py-6 text-muted-foreground text-sm">Loading…</div>}

      {user && !loading && entries.length === 0 && (
        <div className="text-center py-6 border-2 border-dashed border-border rounded-lg" data-testid={`stock-journal-empty-${ticker}`}>
          <p className="text-sm text-foreground font-medium">No journal entries on {ticker} yet.</p>
          <p className="text-xs font-bold text-muted-foreground mt-1">
            Why are you watching this stock? What would make you buy / sell?
          </p>
        </div>
      )}

      {user && !loading && entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(e => (
            <div
              key={e.entry_id}
              className={`rounded-lg border-2 ${HUE[e.entry_type] || HUE.free} bg-muted/20 p-3`}
              data-testid={`stock-journal-entry-${e.entry_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">
                    {TYPE_LABEL[e.entry_type] || 'Note'}
                    {' · '}
                    {new Date(e.created_at).toLocaleDateString()}
                    {e.conviction != null && <span> · conviction {e.conviction}/10</span>}
                  </div>
                  {e.title && <div className="text-sm font-bold">{e.title}</div>}
                  <p className="text-sm text-foreground/90 whitespace-pre-wrap mt-0.5">{e.body}</p>
                  {e.tags && e.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {e.tags.map(t => (
                        <span key={t} className="text-[10px] text-muted-foreground">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button
                    onClick={() => shareEntryToX(e)}
                    className="p-1 rounded hover:bg-sky-100 dark:hover:bg-sky-950/40 text-sky-600"
                    title="Share this entry to X"
                    data-testid={`stock-journal-share-x-${e.entry_id}`}
                  >
                    <Twitter className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setEditing(e)}
                    className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                    title="Edit"
                    data-testid={`stock-journal-edit-${e.entry_id}`}
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleDelete(e.entry_id)}
                    className="p-1 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600"
                    title="Delete"
                    data-testid={`stock-journal-delete-${e.entry_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <JournalEntryModal
          prefill={{ ticker, entry_type: 'free' }}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); fetchEntries(); }}
        />
      )}
      {editing && (
        <JournalEntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchEntries(); }}
        />
      )}
    </div>
  );
}
